import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageSquare, 
  Bot, 
  User, 
  Send, 
  Sparkles, 
  AlertCircle, 
  Clock, 
  CheckCircle, 
  Star,
  Archive,
  MoreVertical,
  Brain,
  Target,
  Activity,
  Zap,
  Users,
  TrendingUp,
  Wifi,
  WifiOff,
  Settings,
  RefreshCw,
  Eye,
  Edit,
  Copy
} from 'lucide-react';
import { Button } from '../../../components/ui/Button/Button';
import { cn } from '../../../utils/cn';

interface Message {
  id: string;
  content: string;
  isOutgoing: boolean;
  timestamp: string;
  confidence?: number;
  aiAnalysis?: {
    sentiment: string;
    intent: string;
    entities: any[];
  };
  metadata?: {
    traceId?: string;
    processingTime?: number;
    model?: string;
  };
}

interface EnhancedConversation {
  id: string;
  conversationId: string;
  contact: {
    name: string;
    email: string;
    company?: string;
    avatar?: string;
  };
  lastMessage: {
    content: string;
    timestamp: string;
    isOutgoing: boolean;
  };
  status: 'active' | 'positive' | 'high-priority' | 'needs-response' | 'closed' | 'escalated' | 'ai-processing';
  priority: 'low' | 'medium' | 'high' | 'critical';
  aiAnalysis?: {
    sentiment: string;
    intent: string;
    confidence: number;
    entities: any[];
    businessContext: any;
    riskFactors: string[];
  };
  suggestedResponses?: any[];
  escalationRecommendation?: any;
  metadata?: {
    traceId?: string;
    processingTime?: number;
    model?: string;
  };
}

interface RealTimeConversationViewProps {
  conversation: EnhancedConversation;
  onGenerateResponse: (preferences?: any) => void;
  onSendResponse: (responseId: string, customText?: string) => void;
  onRequestEscalation: (reason: string, urgency: 'low' | 'medium' | 'high' | 'critical') => void;
}

export const RealTimeConversationView: React.FC<RealTimeConversationViewProps> = ({
  conversation,
  onGenerateResponse,
  onSendResponse,
  onRequestEscalation
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isConnected, setIsConnected] = useState(true);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [showAIAnalysis, setShowAIAnalysis] = useState(true);
  const [escalationDialog, setEscalationDialog] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize with conversation message
  useEffect(() => {
    const initialMessage: Message = {
      id: 'msg-1',
      content: conversation.lastMessage.content,
      isOutgoing: conversation.lastMessage.isOutgoing,
      timestamp: conversation.lastMessage.timestamp,
      confidence: conversation.aiAnalysis?.confidence,
      aiAnalysis: conversation.aiAnalysis ? {
        sentiment: conversation.aiAnalysis.sentiment,
        intent: conversation.aiAnalysis.intent,
        entities: conversation.aiAnalysis.entities
      } : undefined
    };
    
    setMessages([initialMessage]);
  }, [conversation]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'active':
        return { color: 'text-green-400', label: 'Active', icon: Activity };
      case 'positive':
        return { color: 'text-blue-400', label: 'Positive', icon: CheckCircle };
      case 'high-priority':
        return { color: 'text-red-400', label: 'High Priority', icon: AlertCircle };
      case 'needs-response':
        return { color: 'text-orange-400', label: 'Needs Response', icon: Clock };
      case 'escalated':
        return { color: 'text-purple-400', label: 'Escalated', icon: Users };
      case 'ai-processing':
        return { color: 'text-cyan-400', label: 'AI Processing', icon: Bot };
      default:
        return { color: 'text-neutral-400', label: 'Closed', icon: Archive };
    }
  };

  const statusConfig = getStatusConfig(conversation.status);
  const StatusIcon = statusConfig.icon;

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    const messageId = `msg-${Date.now()}`;
    const outgoingMessage: Message = {
      id: messageId,
      content: newMessage,
      isOutgoing: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, outgoingMessage]);
    setNewMessage('');

    // Call the parent handler
    await onSendResponse(messageId, newMessage);
  };

  const handleQuickResponse = (responseText: string) => {
    const messageId = `msg-${Date.now()}`;
    const outgoingMessage: Message = {
      id: messageId,
      content: responseText,
      isOutgoing: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, outgoingMessage]);
    onSendResponse(messageId, responseText);
  };

  const handleEscalation = (reason: string, urgency: 'low' | 'medium' | 'high' | 'critical') => {
    onRequestEscalation(reason, urgency);
    setEscalationDialog(false);
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive': return 'text-green-400 bg-green-500/20';
      case 'negative': return 'text-red-400 bg-red-500/20';
      case 'neutral': return 'text-yellow-400 bg-yellow-500/20';
      default: return 'text-neutral-400 bg-neutral-500/20';
    }
  };

  const quickResponses = [
    "Thank you for your interest! I'd be happy to help.",
    "Let me connect you with the right person for this.",
    "I'll get back to you within 24 hours with more details.",
    "Could you provide more information about your requirements?"
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Conversation Header */}
      <div className="p-6 border-b border-neutral-700/50 bg-gradient-to-r from-neutral-900/50 to-neutral-800/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">
                  {(conversation.contact?.name || 'U').split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase()}
                </span>
              </div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-neutral-900 flex items-center justify-center">
                <StatusIcon className={cn("w-2 h-2", statusConfig.color)} />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white flex items-center">
                {conversation.contact.name}
                {conversation.aiAnalysis && conversation.aiAnalysis.confidence > 0.8 && (
                  <Sparkles className="w-4 h-4 ml-2 text-cyan-400" />
                )}
              </h3>
              <p className="text-neutral-400 text-sm">{conversation.contact.email}</p>
              {conversation.contact.company && (
                <p className="text-neutral-500 text-xs">{conversation.contact.company}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            {/* Status Badge */}
            <span className={cn(
              "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border backdrop-blur-xl",
              statusConfig.color
            )}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {statusConfig.label}
            </span>

            {/* Connection Status */}
            <div className="flex items-center space-x-1">
              {isConnected ? (
                <motion.div
                  animate={{ scale: [1, 1.1, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Wifi className="w-4 h-4 text-green-400" />
                </motion.div>
              ) : (
                <WifiOff className="w-4 h-4 text-red-400" />
              )}
              <span className="text-neutral-400 text-xs">
                {isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAIAnalysis(!showAIAnalysis)}
                className="border-neutral-600 text-neutral-300"
              >
                <Brain className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-neutral-600 text-neutral-300"
              >
                <Star className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEscalationDialog(true)}
                className="border-neutral-600 text-neutral-300"
              >
                <Users className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-neutral-600 text-neutral-300"
              >
                <MoreVertical className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* AI Analysis Summary */}
        {showAIAnalysis && conversation.aiAnalysis && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-4 p-4 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border border-cyan-500/30 rounded-lg"
          >
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-white font-medium flex items-center">
                <Brain className="w-4 h-4 mr-2 text-cyan-400" />
                AI Analysis
              </h4>
              <span className="text-cyan-400 text-sm">
                {Math.round(conversation.aiAnalysis.confidence * 100)}% confidence
              </span>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-neutral-400 text-xs mb-1">Sentiment</p>
                <span className={cn(
                  "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                  getSentimentColor(conversation.aiAnalysis.sentiment)
                )}>
                  {conversation.aiAnalysis.sentiment}
                </span>
              </div>
              <div>
                <p className="text-neutral-400 text-xs mb-1">Intent</p>
                <span className="text-white text-sm capitalize flex items-center">
                  <Target className="w-3 h-3 mr-1 text-purple-400" />
                  {conversation.aiAnalysis.intent}
                </span>
              </div>
              <div>
                <p className="text-neutral-400 text-xs mb-1">Risk Level</p>
                <span className={cn(
                  "text-sm flex items-center",
                  conversation.aiAnalysis.riskFactors.length === 0 ? 'text-green-400' :
                  conversation.aiAnalysis.riskFactors.length <= 2 ? 'text-yellow-400' : 'text-red-400'
                )}>
                  <Activity className="w-3 h-3 mr-1" />
                  {conversation.aiAnalysis.riskFactors.length === 0 ? 'Low' :
                   conversation.aiAnalysis.riskFactors.length <= 2 ? 'Medium' : 'High'}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Message Thread */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <AnimatePresence>
          {messages.map((message, index) => (
            <motion.div
              key={message.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={cn(
                "flex",
                message.isOutgoing ? "justify-end" : "justify-start"
              )}
            >
              <div className={cn(
                "max-w-xs lg:max-w-md px-4 py-3 rounded-2xl shadow-lg",
                message.isOutgoing
                  ? "bg-gradient-to-r from-blue-500 to-purple-600 text-white"
                  : "bg-gradient-to-r from-neutral-800 to-neutral-700 text-white border border-neutral-600/50"
              )}>
                <div className="flex items-start space-x-2">
                  {!message.isOutgoing && (
                    <User className="w-4 h-4 mt-1 text-neutral-400 flex-shrink-0" />
                  )}
                  {message.isOutgoing && (
                    <Bot className="w-4 h-4 mt-1 text-blue-200 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className="text-sm leading-relaxed">{message.content}</p>
                    
                    {/* Message metadata */}
                    <div className="flex items-center justify-between mt-2">
                      <span className={cn(
                        "text-xs",
                        message.isOutgoing ? "text-blue-100" : "text-neutral-400"
                      )}>
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                      
                      {message.confidence && (
                        <span className={cn(
                          "text-xs px-2 py-1 rounded-full",
                          message.isOutgoing ? "bg-blue-600/50" : "bg-neutral-700/50"
                        )}>
                          {Math.round(message.confidence * 100)}% AI
                        </span>
                      )}
                    </div>

                    {/* AI Analysis for incoming messages */}
                    {!message.isOutgoing && message.aiAnalysis && showAIAnalysis && (
                      <div className="mt-2 p-2 bg-neutral-900/50 rounded-lg border border-neutral-600/30">
                        <div className="flex items-center space-x-3 text-xs">
                          <span className={cn(
                            "px-2 py-1 rounded-full",
                            getSentimentColor(message.aiAnalysis.sentiment)
                          )}>
                            {message.aiAnalysis.sentiment}
                          </span>
                          <span className="text-neutral-400">
                            Intent: {message.aiAnalysis.intent}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* AI Processing Indicator */}
        {aiProcessing && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="max-w-xs lg:max-w-md px-4 py-3 rounded-2xl bg-gradient-to-r from-cyan-800/50 to-blue-800/50 border border-cyan-500/30">
              <div className="flex items-center space-x-3">
                <div className="w-4 h-4 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin"></div>
                <p className="text-cyan-400 text-sm">AI is analyzing and generating response...</p>
              </div>
            </div>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Quick Responses */}
      {conversation.suggestedResponses && conversation.suggestedResponses.length > 0 && (
        <div className="px-6 py-3 border-t border-neutral-700/50 bg-gradient-to-r from-neutral-900/30 to-neutral-800/30">
          <p className="text-neutral-400 text-xs mb-2">Quick Responses:</p>
          <div className="flex flex-wrap gap-2">
            {conversation.suggestedResponses.slice(0, 3).map((response, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                onClick={() => handleQuickResponse(typeof response === 'string' ? response : response.text || response.content)}
                className="border-neutral-600 text-neutral-300 hover:bg-blue-500/20 hover:border-blue-500/50 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {typeof response === 'string' 
                  ? response.substring(0, 30) + (response.length > 30 ? '...' : '')
                  : (response.text || response.content || 'Quick response').substring(0, 30) + '...'
                }
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Message Composer */}
      <div className="p-6 border-t border-neutral-700/50 bg-gradient-to-r from-neutral-900/50 to-neutral-800/50">
        <div className="flex items-center space-x-3">
          <div className="flex-1 relative">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder="Type your response..."
              className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent resize-none"
              rows={2}
            />
            <div className="absolute bottom-2 right-2 flex space-x-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onGenerateResponse()}
                className="border-neutral-600 text-neutral-300 hover:bg-cyan-500/20"
              >
                <Sparkles className="w-3 h-3" />
              </Button>
            </div>
          </div>
          <Button
            onClick={handleSendMessage}
            disabled={!newMessage.trim()}
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-6"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>

        {/* Message Actions */}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              size="sm"
              className="border-neutral-600 text-neutral-300 hover:bg-green-500/20"
            >
              <CheckCircle className="w-3 h-3 mr-1" />
              Mark Resolved
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEscalationDialog(true)}
              className="border-neutral-600 text-neutral-300 hover:bg-red-500/20"
            >
              <AlertCircle className="w-3 h-3 mr-1" />
              Escalate
            </Button>
          </div>
          
          <div className="flex items-center space-x-2 text-xs text-neutral-400">
            <span>AI confidence: {Math.round((conversation.aiAnalysis?.confidence || 0) * 100)}%</span>
            {conversation.metadata?.traceId && (
              <span className="px-2 py-1 bg-neutral-700/50 rounded">
                Trace: {conversation.metadata.traceId.substring(0, 8)}...
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Escalation Dialog */}
      {escalationDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-neutral-900 border border-neutral-700 rounded-xl p-6 max-w-md w-full mx-4"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Request Escalation</h3>
            <div className="space-y-4">
              <div>
                <label className="text-neutral-400 text-sm mb-2 block">Reason</label>
                <select className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white">
                  <option>Customer complaint</option>
                  <option>Complex technical issue</option>
                  <option>High-value opportunity</option>
                  <option>Requires human expertise</option>
                </select>
              </div>
              <div>
                <label className="text-neutral-400 text-sm mb-2 block">Urgency</label>
                <select className="w-full px-3 py-2 bg-neutral-800 border border-neutral-700 rounded-lg text-white">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setEscalationDialog(false)}
                  className="border-neutral-600 text-neutral-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleEscalation("Customer complaint", "medium")}
                  className="bg-red-500 hover:bg-red-600"
                >
                  Escalate
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}; 